import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { seawardTool } from '@services/ToolMenuConfigService'

export type StoreFamilyAllowlistOptions =
    'ASSET' |
    'HOUDINI' |
    'IHM' |
    'JANE' |
    'KASPIAN' |
    'KISAN' |
    'MENDEL' |
    'WFM' |
    'DOROTHY' |
    'SSD'

export type StageFamilyAllowlistOptions =
    'TEST' |
    'BETA' |
    'GAMMA' |
    'PROD'

export enum LaunchConfigurationMethod {
    KELPIE = 'kelpie',
    STATIC_ALLOW_LIST = 'staticAllowList',
}

export interface ILaunchConfiguration {
    readonly launchConfigurationMethod: LaunchConfigurationMethod
}

export interface IMigratedStaticAllowListLaunchConfiguration extends ILaunchConfiguration {
    readonly launchConfigurationMethod: LaunchConfigurationMethod.STATIC_ALLOW_LIST
    readonly storeFamilyAllowList: StoreFamilyAllowlistOptions[]
    readonly storeAllowList: string[]
    readonly storeDenyList?: string[]
    readonly storeFamilyDenyList?: StoreFamilyAllowlistOptions[]
    readonly stageAllowList?: StageFamilyAllowlistOptions[]
}

export interface ITool {
    displayName: string
    displayNameStringId: string
    path: string
    // storeAllowList?: string[]
    // storeFamilyAllowList?: StoreFamilyAllowlistOptions[]
    // storeDenyList?: string[]
    // storeFamilyDenyList?: StoreFamilyAllowlistOptions[]
    // launchConfiguration?: ILaunchConfiguration
    metadata: {
        cti: string
        package: string,
    }
    isMigrated?: boolean
    migratedPath?: string
    /** Absolute URL of the registered icon; undefined if the tool has none. */
    icon?: string
    migratedToolLaunchConfiguration?: IMigratedStaticAllowListLaunchConfiguration
    toolName?: string // actually the tool ID
    // runtime parameters only
    translateID?: string
    showMigrated?: boolean
    toolParameters?: { [key: string]: string }
    hashPath?: string
    skipStoreScan?: boolean
    /**
     * From the asset registration: the tool can open read-only for a user who has not
     * scanned into the store it launches against. See isToolViewOnly.
     */
    viewOnlySupported?: boolean
}

export interface IToolState {
    tool: ITool
    previousTool: ITool | null
    lastNonMigratedTool: ITool
    lastMigratedTool: ITool
    openModalCalledWithMigratedTool: boolean
    isToolBarHidden: boolean
}

const initialState: IToolState = {
    tool: seawardTool,
    previousTool: null,
    lastNonMigratedTool: seawardTool,
    lastMigratedTool: seawardTool,
    openModalCalledWithMigratedTool: false,
    isToolBarHidden: false,
}

export const toolSlice = createSlice({
    name: 'toolSlice',
    initialState,
    reducers: {
        switchTool: (state, action: PayloadAction<{ tool: ITool, storesMatch: boolean }>) => {
            if (state.tool?.displayName !== 'Associate Entry' && action.payload.storesMatch) {
                state.previousTool = { ...state.tool }
            }
            /**
             * Redux checks if the state has changed by checking if the reference to the state object has changed.
             * So we use spread operator here to create a new object with a new reference of the action payload
             *
             */
            const newPayload = { ...action.payload.tool }
            if (!action.payload.tool.showMigrated) {
                state.lastNonMigratedTool = newPayload

                // if tool is not migrated, switch migrated iframe to default tool for resetting
                state.lastMigratedTool = seawardTool
            } else {
                state.lastMigratedTool = newPayload
            }

            state.tool = newPayload
        },
        switchToLastNonMigratedTool: (state) => {
            state.tool = state.lastNonMigratedTool
        },
        switchToLastMigratedTool: (state) => {
            state.tool = state.lastMigratedTool
        },
        setOpenModalCalledWithMigratedTool: (state) => {
            state.openModalCalledWithMigratedTool = !!state.tool.showMigrated
        },
        hideToolBar: (state, action: PayloadAction<boolean>) => {
            state.isToolBarHidden = action.payload
        },
        savePreviousTool: (state) => {
            if (state.tool?.displayName !== 'Associate Entry') {
                state.previousTool = { ...state.tool }
            }
        },
        setInitialTool: (state, action: PayloadAction<ITool>) => {
            const newPayload = { ...action.payload }
            // tslint:disable-next-line:triple-equals
            if (state.tool.displayName == 'Welcome') {
                if (!action.payload.showMigrated) {
                    state.lastNonMigratedTool = newPayload

                    // if tool is not migrated, switch migrated iframe to default tool for resetting
                    state.lastMigratedTool = seawardTool
                } else {
                    state.lastMigratedTool = newPayload
                }

                state.tool = newPayload
            }
        },
    },
})

export const {
    setInitialTool,
    switchTool,
    switchToLastNonMigratedTool,
    switchToLastMigratedTool,
    setOpenModalCalledWithMigratedTool,
    hideToolBar,
    savePreviousTool,
} = toolSlice.actions

export default toolSlice.reducer
